import { tokenize } from "@nijiurachan/pua-command";
import { find, type Opts } from "linkifyjs";
import { convertAffiliateUrl } from "@/shared/lib/affiliateLinks";

export interface TextSegment {
  type: "text";
  content: string;
}

export interface LinkSegment {
  type: "link";
  content: string;
  href: string;
}

/** バックエンドが焼き込んだPUAマーカー由来の装飾テキスト (dice結果・伏せ字等) */
export interface StyledSegment {
  type: "styled";
  content: string;
  /** pua-command の定義に対応するCSSクラス。ネスト時は外側から順に並ぶ */
  classNames: readonly string[];
}

export type Segment = TextSegment | LinkSegment | StyledSegment;

/** 文中リンク検出時のオプション */
const linkifyOpts: Opts = {
  validate: {
    url: (value: string) => {
      try {
        // プロトコル(http/https)が付いていないドメイン名はリンク化から除外する(例外が出る)
        const url = new URL(value);

        const host = url.hostname.replace(/\.$/, "");
        // localhostは除外
        if (host === "localhost" || host.endsWith(".localhost")) {
          return false;
        }
        // IPv4も除外
        if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(host)) {
          return false;
        }
        // IPv6も除外
        if (host.includes(":")) {
          return false;
        }
        return url.protocol === "http:" || url.protocol === "https:";
      } catch {
        return false;
      }
    },
  },
};

/**
 * テキストを装飾(PUAマーカー)、リンク、プレーンテキストのセグメントに分割する。
 *
 * 先にPUAマーカーをトークン化し、無装飾のランにだけURL検出(linkifyjs)を適用する
 * (伏せ字やdice結果の内側はリンク化しない)。tokenize が出力からPUAを全除去する
 * ため、セグメントの content にPUAは残らない。
 */
export function segmentize(text: string): Segment[] {
  if (!text) return [];

  const segments: Segment[] = [];
  for (const run of tokenize(text)) {
    if (run.classNames.length > 0) {
      segments.push({
        type: "styled",
        content: run.text,
        classNames: run.classNames,
      });
    } else {
      segments.push(...linkifySegments(run.text));
    }
  }
  return segments;
}

/** プレーンテキストをリンクとテキストのセグメントへ分割する */
function linkifySegments(text: string): Segment[] {
  if (!text) return [];

  const linkMatches = find(text, "url", linkifyOpts);
  const segments: Segment[] = [];
  let cursor = 0;

  for (const match of linkMatches) {
    if (match.start < cursor) continue;

    if (match.start > cursor) {
      segments.push({ type: "text", content: text.slice(cursor, match.start) });
    }
    const affiliateUrl = convertAffiliateUrl(match.value);
    segments.push(
      affiliateUrl === match.value
        ? { type: "link", content: match.value, href: match.href }
        : { type: "link", content: affiliateUrl, href: affiliateUrl },
    );
    cursor = match.end;
  }

  if (cursor < text.length) {
    segments.push({ type: "text", content: text.slice(cursor) });
  }

  return segments;
}
